/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskEvalsSaveReview } from "../funcs/riskEvalsSaveReview.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RiskPolicyEvalReview } from "../models/components/riskpolicyevalreview.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SaveRiskEvalReviewRequest,
  SaveRiskEvalReviewSecurity,
} from "../models/operations/saveriskevalreview.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskSaveEvalReviewMutationVariables = {
  request: SaveRiskEvalReviewRequest;
  security?: SaveRiskEvalReviewSecurity | undefined;
  options?: RequestOptions;
};

export type RiskSaveEvalReviewMutationData = RiskPolicyEvalReview;

export type RiskSaveEvalReviewMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * saveRiskEvalReview risk
 *
 * @remarks
 * Record (or replace) the current reviewer's ground-truth verdict for one chat session under a prompt-based policy. This is the durable regression set the eval workbench scores the live guardrail against. Upserts: a reviewer has at most one verdict per session per policy.
 */
export function useRiskSaveEvalReviewMutation(
  options?: MutationHookOptions<
    RiskSaveEvalReviewMutationData,
    RiskSaveEvalReviewMutationError,
    RiskSaveEvalReviewMutationVariables
  >,
): UseMutationResult<
  RiskSaveEvalReviewMutationData,
  RiskSaveEvalReviewMutationError,
  RiskSaveEvalReviewMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskSaveEvalReviewMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskSaveEvalReview(): MutationKey {
  return ["@gram/client", "evals", "saveReview"];
}

export function buildRiskSaveEvalReviewMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskSaveEvalReviewMutationVariables,
  ) => Promise<RiskSaveEvalReviewMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskSaveEvalReview(),
    mutationFn: function riskSaveEvalReviewMutationFn({
      request,
      security,
      options,
    }): Promise<RiskSaveEvalReviewMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskEvalsSaveReview(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
